// src/utils/syncMessage.js — what to say when the InSite site sync fails.
// ---------------------------------------------------------------------------
// 2026-08-17. The Locations screen auto-syncs the tenant's InSite sites on
// mount. When that failed it printed the API's own error verbatim, so Brett got
//
//     ⚠ Not signed in.
//
// across the top of a page that was listing his locations, with his name in the
// header. He was signed in. What had failed was one background call.
//
// Two rules come out of that, and they are what this file exists to hold:
//
//   1. SAY WHAT FAILED. The sync failed — so the sentence is about the sync,
//      never about the user's session in general.
//   2. SAY WHAT IS STILL GOOD. The saved locations are untouched by a failed
//      sync. A banner that does not say so reads as "this whole page is
//      broken", which is how a true statement becomes a misleading one.
//
// One sentence each. A banner is not the place to explain the suite.

const DEFAULT = 'Couldn’t reach InSite to sync your sites — the locations below are unaffected.';

/**
 * Turn a failed sync into a sentence worth showing.
 * @param {Error & {status?: number}} err
 * @returns {string}
 */
export function syncFailureMessage(err) {
  const status = err && typeof err.status === 'number' ? err.status : null;

  switch (status) {
    case 401:
      // The DropPin session is fine — the page rendered. It is the InSite
      // link behind the sync that is not authenticated.
      return 'Your InSite connection needs signing in again, so your sites weren’t synced — the locations below are unaffected.';
    case 403:
      return 'Your InSite account doesn’t have DropPin access, so your sites weren’t synced — the locations below are unaffected.';
    case 404:
      return 'InSite didn’t recognise this workspace, so your sites weren’t synced — the locations below are unaffected.';
    case 429:
      return 'InSite is rate-limiting us, so your sites weren’t synced — try again shortly.';
    case 503:
      return 'InSite’s sign-in service is unavailable, so your sites weren’t synced — try again shortly.';
    default:
      break;
  }
  if (status && status >= 500) {
    return 'InSite had a server error, so your sites weren’t synced — the locations below are unaffected.';
  }
  return DEFAULT;
}

export const DEFAULT_SYNC_FAILURE = DEFAULT;
